/*********************
 * Mobillor Technologies Pvt. ltd. CONFIDENTIAL
 * __________________
 *
 *  Mobillor Technologies Pvt. Ltd.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Mobillor Technologies Pvt. Ltd.
 * The intellectual and technical concepts contained
 * herein are proprietary to Mobillor Technologies Pvt. Ltd.
 * may be covered by Rebublic Of India and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Mobillor Technologies Pvt. Ltd.
 */

import { useEffect, useState } from "react";
import { connect } from "react-redux";

// Router Components
import { useNavigate } from "react-router-dom";

// Formik and Yup
import { useFormik } from "formik";
import * as Yup from "yup";

// Utils component
import TextError from "utils/TextError";

// Dashboard components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDCard from "components/MDCard";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// Dashboard example components
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

// @mui material components
import Alert from "@mui/material/Alert";
import Backdrop from "@mui/material/Backdrop";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";

// @mui material icons
import CloseIcon from "@mui/icons-material/Close";

// Functions from store
import {
  addZoneToWarehouse,
  getWarehouses,
  updateWarehouse,
} from "../../store";

// Custom css
import "../../index.css";

// Cookies
import Cookies from "universal-cookie";

const cookies = new Cookies();

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  boxShadow: 24,
  p: 1,
  pt: 3,
};

const styleForStatus = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  boxShadow: 24,
  p: 2,
  pt: 3,
};

const Warehouse = ({
  getWarehouses,
  warehouses,
  updateWarehouse,
  addZoneToWarehouse,
}) => {
  const theme = createTheme({
    components: {
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            // Controls default (unchecked) color for the thumb
            color: "white",
          },
          colorPrimary: {
            "&.Mui-checked": {
              // Controls checked color for the thumb
              color: "rgb(25, 118, 210)",
            },
          },
          track: {
            // Controls default (unchecked) color for the track
            opacity: 0.5,
            backgroundColor: "grey",
            ".Mui-checked.Mui-checked + &": {
              // Controls checked color for the track
              opacity: 0.6,
              backgroundColor: "rgb(25, 118, 210)",
            },
          },
        },
      },
    },
  });

  let loginDetails = cookies.get("loginDetailsForWMS");
  let jwtToken = loginDetails?.jwt;
  let roleId = loginDetails.data.roleId;
  let role = loginDetails.data.roleName;
  let dynamicColumnDetails = cookies.get("dynamic-column-data");
  let userId = loginDetails.data.userId;

  useEffect(() => {
    getWarehouses(jwtToken);
  }, []);

  const [state, setState] = useState({
    columns: [
      { Header: "Warehouse Code", accessor: "warehouseCode", align: "left" },
      { Header: "Warehouse", accessor: "warehouse", align: "left" },
      { Header: "Status", accessor: "status", align: "left" },
      { Header: "Action", accessor: "action", align: "left" },
    ],
    rows: [],
  });

  const { columns, rows } = state;

  const navigate = useNavigate();

  const [warehouseIdSelected, setWarehouseIdSelected] = useState("");

  const [openWarehouseStatusModal, setOpenWarehouseStatusModal] =
    useState(false);

  const handleOpenWarehouseStatusModal = () => {
    setOpenWarehouseStatusModal(true);
  };
  const handleCloseWarehouseStatusModal = () => {
    setOpenWarehouseStatusModal(false);
  };

  const [singleWarehouseDate, setSingleWarehouseDate] = useState({});

  const updateStatus = async () => {
    singleWarehouseDate["isActive"] = !singleWarehouseDate.isActive;
    let res = await updateWarehouse(singleWarehouseDate, jwtToken);

    if (res.status) {
      await getWarehouses();
      handleCloseWarehouseStatusModal();
    }
  };

  useEffect(() => {
    let tempWarehouses = [];
    let data = warehouses.warehouses ? warehouses.warehouses : [];
    data.map((warehouse) => {
      if (warehouse.type === "s") {
        warehouse["warehouseCode"] = warehouse.warehouseCode;
        warehouse["warehouse"] = warehouse.warehouseName;
        warehouse["status"] = (
          <ThemeProvider theme={theme}>
            <Switch
              checked={warehouse.isActive === 1 ? true : false}
              onChange={() => {
                handleOpenWarehouseStatusModal();
                setSingleWarehouseDate(warehouse);
              }}
            />
          </ThemeProvider>
        );
        warehouse["action"] = (
          <div>
            {warehouse.isActive === 1 ? (
              <Tooltip title="View locations">
                <MDButton
                  type="button"
                  variant="gradient"
                  color="success"
                  iconOnly
                  style={{
                    marginRight: 5,
                  }}
                  onClick={() => {
                    navigate(`/${warehouse.type}/${warehouse.id}`);
                  }}
                >
                  <Icon> visibility_icon </Icon>
                </MDButton>
              </Tooltip>
            ) : (
              <Tooltip title="Disabled">
                <MDButton
                  type="button"
                  variant="gradient"
                  style={{
                    background: "grey",
                    color: "white",
                    opacity: 0.5,
                    marginRight: 5,
                  }}
                  disabled
                  iconOnly
                >
                  <Icon> visibility_icon </Icon>
                </MDButton>
              </Tooltip>
            )}
            {role !== "admin" ? (
              <Tooltip title="Add Zone">
                <MDButton
                  type="button"
                  variant="gradient"
                  iconOnly
                  style={{
                    background: "grey",
                    color: "white",
                    opacity: 0.5,
                    marginLeft: { xs: -30, sm: -30, md: 10, lg: 10, xl: 10 },
                  }}
                  disabled
                >
                  <Icon> add </Icon>
                </MDButton>
              </Tooltip>
            ) : (
              <Tooltip title="Add Zone">
                <MDButton
                  type="button"
                  variant="gradient"
                  color="info"
                  iconOnly
                  style={{
                    marginLeft: { xs: -30, sm: -30, md: 10, lg: 10, xl: 10 },
                  }}
                  onClick={() => {
                    setAddZoneModal(true);
                    setWarehouseIdSelected(warehouse.id);
                  }}
                >
                  <Icon> add </Icon>
                </MDButton>
              </Tooltip>
            )}
          </div>
        );
        tempWarehouses.push(warehouse);
      }
    });
    setState({ ...state, rows: tempWarehouses });
  }, [warehouses]);

  let loading = warehouses.loading;

  const [isSuccessAddZone, setIsSuccessAddZone] = useState(false);
  const [isErrorAddZone, setIsErrorAddZone] = useState(false);

  useEffect(() => {
    if (isErrorAddZone) {
      setTimeout(() => {
        setIsErrorAddZone(false);
      }, 3000);
    }
  }, [isErrorAddZone]);

  useEffect(() => {
    if (isSuccessAddZone) {
      setTimeout(() => {
        setIsSuccessAddZone(false);
      }, 3000);
    }
  }, [isSuccessAddZone]);

  const [addZoneModal, setAddZoneModal] = useState(false);
  const handleAddZoneModal = () => {
    setAddZoneModal(!addZoneModal);
  };

  const initialValueForAddZone = {
    zoneName: "",
  };

  const validationSchemaForAddZone = Yup.object({
    zoneName: Yup.string().required("Enter the Zone Name"),
  });

  const onSubmitForAddZone = async (values, { resetForm }) => {
    const payload = {
      zoneName: values.zoneName,
      warehouseId: warehouseIdSelected,
    };

    let res = await addZoneToWarehouse(payload);

    if (res.status) {
      setIsSuccessAddZone(true);
      await getWarehouses();
    }
    if (!res.status) {
      setIsErrorAddZone(true);
    }
    resetForm();
  };

  const formik = useFormik({
    initialValues: initialValueForAddZone,
    onSubmit: onSubmitForAddZone,
    validationSchema: validationSchemaForAddZone,
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {!loading ? (
        <MDBox pt={2} pb={3}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={3}
                  mb={-6}
                >
                  <MDBox>
                    <MDTypography variant="h4" gutterBottom>
                      Source Warehouse
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox pt={3}>
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={true}
                    canSearch={true}
                    entriesPerPage={{
                      defaultValue: 5,
                      entries: [5, 10, 15, 20, 25, 30],
                    }}
                    showTotalEntries={true}
                    noEndBorder
                    options={{ draggable: false }}
                  />
                </MDBox>

                <Modal
                  open={openWarehouseStatusModal}
                  onClose={handleCloseWarehouseStatusModal}
                  aria-labelledby="modal-modal-title"
                  aria-describedby="modal-modal-description"
                >
                  <MDCard sx={styleForStatus}>
                    <MDTypography>
                      Are you sure you want to change the status of this
                      warehouse?
                    </MDTypography>
                    <MDBox mt={2}>
                      <MDButton
                        style={{ width: "48%", marginRight: "0.5rem" }}
                        color="info"
                        variant="outlined"
                        onClick={handleCloseWarehouseStatusModal}
                      >
                        Cancel
                      </MDButton>
                      <MDButton
                        style={{ width: "48%", marginLeft: "0.5rem" }}
                        color="success"
                        onClick={updateStatus}
                      >
                        Yes
                      </MDButton>
                    </MDBox>
                  </MDCard>
                </Modal>
                <Modal
                  open={addZoneModal}
                  onClose={handleAddZoneModal}
                  aria-labelledby="modal-modal-title"
                  aria-describedby="modal-modal-description"
                >
                  <MDCard sx={style}>
                    <MDBox ml={3} mt={-1} display="flex" alignItems="center">
                      <MDTypography
                        variant="h4"
                        fontWeight="medium"
                        flexGrow={1}
                      >
                        Add Zone
                      </MDTypography>
                      <MDBox>
                        <IconButton
                          aria-label="close"
                          color="inherit"
                          onClick={handleAddZoneModal}
                        >
                          <CloseIcon />
                        </IconButton>
                      </MDBox>
                    </MDBox>
                    <hr style={{ marginTop: "0.5rem", marginBottom: "1rem" }} />

                    <MDBox pt={1} pb={3} px={3}>
                      <MDBox
                        component="form"
                        role="form"
                        onSubmit={formik.handleSubmit}
                      >
                        <MDBox mb={2}>
                          <MDTypography
                            variant="h6"
                            style={{ fontWeight: 500 }}
                          >
                            Zone Name
                          </MDTypography>
                          <MDInput
                            type="text"
                            name="zoneName"
                            fullWidth
                            autoComplete="off"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            value={formik.values.zoneName}
                            error={
                              formik.touched.zoneName &&
                              formik.errors.zoneName &&
                              true
                            }
                          />
                          {formik.touched.zoneName &&
                            formik.errors.zoneName && (
                              <TextError msg={formik.errors.zoneName} />
                            )}
                        </MDBox>

                        <MDBox>
                          <Collapse in={isErrorAddZone}>
                            <Alert
                              severity="error"
                              action={
                                <IconButton
                                  aria-label="close"
                                  color="inherit"
                                  size="small"
                                  onClick={() => {
                                    setIsErrorAddZone(false);
                                  }}
                                >
                                  <CloseIcon fontSize="inherit" />
                                </IconButton>
                              }
                            >
                              Could not add the Zone
                            </Alert>
                          </Collapse>
                          <Collapse in={isSuccessAddZone}>
                            <Alert
                              severity="success"
                              action={
                                <IconButton
                                  aria-label="close"
                                  color="inherit"
                                  size="small"
                                  onClick={() => {
                                    setIsSuccessAddZone(false);
                                  }}
                                >
                                  <CloseIcon fontSize="inherit" />
                                </IconButton>
                              }
                            >
                              Zone added successfully
                            </Alert>
                          </Collapse>
                        </MDBox>
                        <MDBox mt={3}>
                          <MDButton
                            style={{ backgroundColor: "red", float: "left" }}
                            color="info"
                            fullWidth
                            type="submit"
                            disabled={!formik.isValid}
                          >
                            Add Zone
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </MDCard>
                </Modal>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      ) : (
        <Backdrop
          sx={{ color: "blue", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Footer />
    </DashboardLayout>
  );
};

const mapStateToProps = (state) => {
  return {
    warehouses: state.warehouses,
  };
};

const mapStateToDispatch = (dispatch) => {
  return {
    getWarehouses: (jwtToken) => dispatch(getWarehouses(jwtToken)),
    updateWarehouse: (payload, jwtToken) =>
      dispatch(updateWarehouse(payload, jwtToken)),
    addZoneToWarehouse: (payload, jwtToken) =>
      dispatch(addZoneToWarehouse(payload, jwtToken)),
  };
};

export default connect(mapStateToProps, mapStateToDispatch)(Warehouse);
